/**
 * EMAIL: AS REGRAS DO QUE SAI PARA FORA (11/10/2026, medição do módulo Email). Comunicado a cliente, template Padrão das
 * automações e conta de envio. Funções puras que as telas usam e os testes cobram dos dois lados.
 */

type Destino = { clientId: number; email: string; name: string };
type Cliente = { id: number; name?: string; tradeName?: string; companyName?: string; status?: string };

const nomeDoCliente = (c?: Cliente) => (c ? c.name || c.tradeName || c.companyName || `Cliente #${c.id}` : '');

export function horaDeBrasilia(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------- Comunicado (D1, D2, D3)

/**
 * D2: o confirm diz o que vai sair. A lista que sai é exatamente a marcada (o backend não recalcula), mas a marcação
 * pode incluir cliente não ativo (filtro "Todos") e e-mails que saíram da vista numa troca de filtro.
 */
export function confirmacaoDoComunicado(destinos: Destino[], clientes: Cliente[], idsVisiveis: Set<number>) {
  const porId = new Map(clientes.map((c) => [c.id, c]));
  const ids = Array.from(new Set(destinos.map((d) => d.clientId)));
  const naoAtivos = ids.filter((id) => (porId.get(id)?.status || '') !== 'Ativo').length;
  const foraDaVista = destinos.filter((d) => !idsVisiveis.has(d.clientId)).length;
  const nomes = ids.slice(0, 3).map((id) => nomeDoCliente(porId.get(id)) || `#${id}`);
  const resto = ids.length - nomes.length;
  const avisos = [
    naoAtivos > 0 ? `${naoAtivos} ${naoAtivos === 1 ? 'cliente não está ativo' : 'clientes não estão ativos'}.` : null,
    foraDaVista > 0 ? `${foraDaVista} e-mail${foraDaVista === 1 ? ' está' : 's estão'} fora do filtro atual (marcados antes de trocar o filtro).` : null,
  ].filter(Boolean);
  return {
    title: 'Enviar o comunicado?',
    message: `${destinos.length} e-mail(s) de ${ids.length} cliente(s): ${nomes.join(', ')}${resto > 0 ? ` e mais ${resto}` : ''}.`,
    consequence: [...avisos, 'Sai para fora: não há como desfazer.'].join(' '),
    confirmLabel: 'Enviar',
  };
}

export type EnvioDaSessao = { assunto: string; quando: string; n: number };

/** D1: o mesmo assunto já saiu nesta sessão? */
export function envioAnteriorDoAssunto(envios: EnvioDaSessao[], assunto: string): EnvioDaSessao | null {
  const a = assunto.trim().toLowerCase();
  return [...envios].reverse().find((e) => e.assunto.trim().toLowerCase() === a) || null;
}

export function confirmacaoDoReenvio(e: EnvioDaSessao) {
  return {
    title: 'Este assunto já saiu',
    message: `"${e.assunto}" saiu às ${horaDeBrasilia(e.quando)} para ${e.n} e-mail(s) nesta sessão.`,
    consequence: 'Enviar de novo manda outra vez para quem estiver marcado, inclusive quem já recebeu.',
    confirmLabel: 'Enviar de novo',
    danger: true,
  };
}

export function fraseDoEnviado(quando: string, n: number): string {
  return `Enviado às ${horaDeBrasilia(quando)} para ${n} e-mail(s). A seleção foi limpa.`;
}

/** A seleção como chave estável: a trava do D3 compara a seleção de agora com a do lote que perdeu o rastro. */
export function chaveDaSelecao(destinos: Destino[]): string {
  return destinos.map((d) => `${d.clientId}|${d.email.trim().toLowerCase()}`).sort().join(',');
}

/** D3: o servidor não sabe mais do lote (reiniciou). Nada diz quantos saíram: a prova é o Log. */
export const FRASE_DO_RASTRO_PERDIDO = 'Perdi o acompanhamento deste envio: o servidor não sabe mais dele. Confira no Log de e-mails antes de reenviar. Com a mesma seleção, o envio fica travado.';

// ---------------------------------------------------------------- Template Padrão (D5)

type Template = { id: number; name: string; type?: string; isDefault?: boolean; isActive?: boolean };

/** O template Padrão de um tipo é o que as automações daquele tipo mandam (email.service → getDefaultTemplate). */
export function confirmacaoDeSalvarPadrao(form: { name: string; type?: string; isDefault?: boolean }, original: Template | null, todos: Template[], rotuloDoTipo: string) {
  if (!form.isDefault) return null;
  const atual = todos.find((t) => t.type === form.type && t.isDefault && t.id !== original?.id);
  return {
    title: 'Salvar o Padrão?',
    message: atual
      ? `"${form.name}" passa a ser o Padrão de ${rotuloDoTipo} no lugar de "${atual.name}".`
      : `"${form.name}" é o Padrão de ${rotuloDoTipo}.`,
    consequence: `É o texto que sai nos e-mails automáticos de ${rotuloDoTipo} a partir de agora.`,
    confirmLabel: 'Salvar',
  };
}

/** Excluir o Padrão que é o único ativo do tipo faria as automações caírem no texto embutido do sistema, calado. */
export function porqueNaoExcluirTemplate(t: Template, todos: Template[], rotuloDoTipo: string): string | null {
  if (!t.isDefault) return null;
  const outrosAtivos = todos.filter((x) => x.type === t.type && x.id !== t.id && x.isActive !== false);
  if (outrosAtivos.length > 0) return null;
  return `"${t.name}" é o Padrão e o único ativo de ${rotuloDoTipo}: sem ele, os e-mails automáticos desse tipo saem com o texto embutido do sistema. Marque outro como Padrão antes de excluir.`;
}

export function confirmacaoDeExcluirTemplate(t: Template, todos: Template[], rotuloDoTipo: string): string {
  const cabeca = 'Excluir o template de e-mail?';
  if (!t.isDefault) return [cabeca, `"${t.name}". Não é o Padrão: nenhuma automação o usa.`].join('\n\n');
  const outro = todos.find((x) => x.type === t.type && x.id !== t.id && x.isActive !== false);
  return [cabeca, `"${t.name}" é o Padrão de ${rotuloDoTipo}. Os e-mails automáticos desse tipo passam a sair por outro template ativo${outro ? ` (por exemplo "${outro.name}")` : ''}, escolhido pelo servidor.`].join('\n\n');
}

// ---------------------------------------------------------------- Conta de envio (D6)

type Conta = { id: number; name: string; isActive?: boolean; purposes?: string[] };

/** D6: duas contas ativas na mesma finalidade = remetente imprevisível (o servidor pega uma sem ordem). */
export function finalidadesRepetidas(form: { isActive?: boolean; purposes?: string[] }, editando: Conta | null, contas: Conta[], rotulo: (p: string) => string): string | null {
  if (form.isActive === false) return null;
  const outras = contas.filter((c) => c.id !== editando?.id && c.isActive !== false);
  const repetidas = (form.purposes || []).map((p) => ({ p, conta: outras.find((c) => (c.purposes || []).includes(p)) })).filter((x) => x.conta);
  if (repetidas.length === 0) return null;
  return `Passa a haver duas contas ativas para ${repetidas.map((r) => `"${rotulo(r.p)}" (com ${r.conta!.name})`).join(', ')}: o servidor escolhe uma sem ordem, e o remetente fica imprevisível a cada envio.`;
}
